/**
 * Use the product rail for favorites dashboard to showcase products that were favorited
 *
 * @module views/components/ProductRailHorizontalCompactFavorites
 * @memberof -Common
 */
import './ProductRailHorizontalCompactFavorites.scss';

import React, { useRef } from 'react';

import PropTypes from 'prop-types';

import AsyncComponent from '@ulta/core/components/AsyncComponent/AsyncComponent';
import { Rail, Slide } from '@ulta/core/components/Rail/Rail';
import { hasItems } from '@ulta/core/utils/array/array';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import carouselUtils from '@ulta/utils/carouselUtils/carouselUtils';

/**
 * Represents a ProductRailHorizontalCompactFavorites component
 *
 * @method
 * @param {ProductRailHorizontalCompactFavoritesProps} props - React properties passed from composition
 * @returns ProductRailHorizontalCompactFavorites
 */
export const ProductRailHorizontalCompactFavorites = function( props ){
  const {
    title,
    countLabel,
    items,
    alignment,
    previousClickAction,
    nextClickAction,
    viewAllAction,
    componentKey,
    moduleName,
    carouselAccessibility,
    carouselCardAccessibility,
    carouselDetailsAccessibility,
    nextAccessibility,
    previousAccessibility,
    dataCapture
  } = props;

  const railRef = useRef( null );

  const sliderPerViewXL = 3;
  const slidesPerGroupXL = 2;
  const sliderPerViewLG = 4;
  const slidesPerGroupLG = 4;
  const sliderPerViewMD = 4;
  const slidesPerGroupMD = 4;
  const sliderPerViewSM = 2.4;
  const slidesPerGroupSM = 1;

  /* Interpolating accesibility strings */
  const newCarouselAccessibility = carouselUtils.handleTokenizedString( carouselAccessibility, [title] );
  const newCarouselCardAccessibility = carouselUtils.handleTokenizedString( carouselCardAccessibility, [title] );
  const newCarouselDetailsAccessibility = carouselUtils.handleTokenizedString( carouselDetailsAccessibility, [title] );

  /* ADA Objects */
  const _railADAmeta = {
    carouselAccessibility: newCarouselAccessibility,
    carouselCardAccessibility: newCarouselCardAccessibility,
    carouselDetailsAccessibility: newCarouselDetailsAccessibility
  };

  const _railMeta = {
    countLabel: countLabel,
    nextAccessibility: nextAccessibility,
    previousAccessibility: previousAccessibility
  };

  if( !hasItems( items ) ){
    return null;
  }

  return (
    <div className='ProductRailHorizontalCompactFavorites'>
      <Rail
        railRef={ railRef }
        title={ title }
        items={ items }
        headline={ title }
        subheadline={ countLabel }
        viewAllAction={ viewAllAction }
        dataCapture={ dataCapture }
        _railADAmeta={ _railADAmeta }
        _railMeta={ _railMeta }
        alignment={ alignment }
        previousClickAction={ previousClickAction }
        nextClickAction={ nextClickAction }
        sliderPerViewXL={ sliderPerViewXL }
        slidesPerGroupXL={ slidesPerGroupXL }
        sliderPerViewLG={ sliderPerViewLG }
        slidesPerGroupLG={ slidesPerGroupLG }
        sliderPerViewMD={ sliderPerViewMD }
        slidesPerGroupMD={ slidesPerGroupMD }
        slidesPerGroupSM={ slidesPerGroupSM }
        sliderPerViewSM={ sliderPerViewSM }
      >
        { items?.length > 0 && items?.map( ( item, cardsIndex ) => (
          <Slide key={ `ProductCardCompact-${ componentKey }-${ moduleName }-${ cardsIndex }}` }>
            <AsyncComponent
              componentKey={ componentKey }
              moduleName='ProductCardCompact'
              { ...spreadProps( { ...item } ) }
            />
          </Slide>
        ) ) }
      </Rail>
    </div>
  );
};

/**
 * Default values for passed properties
 * @type {ProductRailHorizontalCompactFavoritesProps}
 * @property {object} INTERSECTION_OBSERVER_OPTIONS - The default initial values for intersection observer.
 */
export const defaultProps = {
  alignment: 'bottom'
};
/**
 * Property type definitions
 *
 * @typedef ProductRailHorizontalCompactFavoritesProps
 * @type {object}
 * @property {string} title - Sets the headline title.
 * @property {string} subtitle - Sets the subtitle.
 * @property {object} viewAllAction - sets the url for action label.
 * @property {array} items - Set the Item array.
 * @property {string} alignment - sets the alignment for the text.
 * @property {object} previousClickAction - The prop that holds the action for the previous button
 * @property {object} nextClickAction - The prop that holds the action for the next button.
 * @property {object} _railADAmeta - sets carouselAccessibility
 * @property {object} _railMeta - sets nextAccessibility, previousAccessibility
 */

export const propTypes =  {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  viewAllAction: PropTypes.object,
  items: PropTypes.array,
  alignment: PropTypes.string,
  previousClickAction: PropTypes.object,
  nextClickAction: PropTypes.object,
  moduleName: PropTypes.string,
  componentKey: PropTypes.string,
  carouselAccessibility: PropTypes.string,
  carouselCardAccessibility: PropTypes.string,
  carouselDetailsAccessibility: PropTypes.string,
  nextAccessibility: PropTypes.string,
  previousAccessibility: PropTypes.string
};

ProductRailHorizontalCompactFavorites.propTypes = propTypes;
ProductRailHorizontalCompactFavorites.defaultProps = defaultProps;

export default ProductRailHorizontalCompactFavorites;